import React, { useEffect, useState } from 'react';
import { Check, Loader2, Plus, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { useI18n } from '../../contexts/I18nContext';

export const AddLanguageDialog = ({ open, onOpenChange, languages, activeIds, hiddenIds, loading, error, busy, onRetry, onAdd, onRestore }) => {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  useEffect(() => { if (open) setSearch(''); }, [open]);
  const query = search.trim().toLocaleLowerCase();
  const filtered = languages.filter(lang => [lang.name, lang.native_name, lang.code].some(value => (value || '').toLocaleLowerCase().includes(query)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-[min(80dvh,640px)] flex flex-col overflow-hidden" data-testid="add-language-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle data-testid="add-language-dialog-title">{t('dashboard.addLanguage')}</DialogTitle></DialogHeader>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('dashboard.searchLanguages')} aria-label={t('dashboard.searchLanguages')} data-testid="add-language-search" />
        {loading ? <div className="flex items-center gap-2 text-sm" role="status" data-testid="add-language-loading"><Loader2 className="h-4 w-4 animate-spin" />{t('common.loading')}</div> : error ? (
          <div role="alert" data-testid="add-language-error"><p className="text-sm text-destructive">{t('dashboard.languagesUnavailable')}</p><Button variant="outline" onClick={onRetry} data-testid="add-language-retry">{t('dashboard.retry')}</Button></div>
        ) : <>
          <p className="text-xs text-muted-foreground" data-testid="add-language-count">{filtered.length} / {languages.length}</p>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1" data-testid="add-language-list">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filtered.map(lang => {
                const hidden = hiddenIds.includes(lang.language_id);
                const added = activeIds.includes(lang.language_id) && !hidden;
                const Icon = added ? Check : hidden ? RotateCcw : Plus;
                return (
                  <Button key={lang.language_id} variant="outline" className="h-auto min-h-12 justify-start gap-2 whitespace-normal px-3 py-2 text-start" disabled={busy || added} onClick={() => hidden ? onRestore(lang.language_id) : onAdd(lang.language_id)} data-testid={`add-lang-${lang.code}`} data-language-id={lang.language_id}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1 break-words text-xs">{lang.name}{lang.native_name && lang.native_name !== lang.name && <span className="block text-muted-foreground" dir="auto">{lang.native_name}</span>}</span>
                    {(added || hidden) && <span className="text-[10px] text-muted-foreground">{t(added ? 'dashboard.languageAdded' : 'dashboard.restoreLanguage')}</span>}
                  </Button>
                );
              })}
            </div>
            {filtered.length === 0 && <p className="py-6 text-sm text-muted-foreground" data-testid="add-language-empty">{t('dashboard.noMatchingLanguages')}</p>}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};